import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  ChatInfo,
  ChatManagementHooks,
  createNewChat,
  deleteChat as deleteChatApi,
  ensureChatExists as ensureChatExistsApi,
  switchToChat as switchToChatApi,
} from "../Tool/ChatManagement";

export interface UseAgentChatOptions {
  autoSelectLatest?: boolean;
  onChatIdPersist?: (chatId: string | null) => Promise<void> | void;
}

export interface UseAgentChatResult {
  currentChatId: string | null;
  messages: any[];
  availableChats: ChatInfo[];
  isConversationLoading: boolean;
  handleChatIdChange: (chatId: string | null) => Promise<void>;
  handleNewMessage: (message: any) => void;
  fetchAvailableChats: () => Promise<void>;
  loadChatHistory: (chatId: string) => Promise<void>;
  createChat: () => Promise<string | null>;
  switchChat: (newChatId: string) => void;
  deleteChat: (chatId: string) => Promise<boolean>;
  ensureChatExists: (chatId?: string) => Promise<string>;
}

export function useAgentChat(
  workproductId: string,
  workproductType: string,
  options?: UseAgentChatOptions
): UseAgentChatResult {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [availableChats, setAvailableChats] = useState<ChatInfo[]>([]);
  const [isConversationLoading, setIsConversationLoading] = useState(
    Boolean(workproductId)
  );

  const hasAutoSelectedRef = useRef<boolean>(false);
  const didRetryChatsRef = useRef<boolean>(false);
  const suppressNextHistoryLoadRef = useRef<boolean>(false);
  const didFlushThisFrameRef = useRef<boolean>(false);
  const resetFlushFlagScheduledRef = useRef<boolean>(false);
  const previousChatIdRef = useRef<string | null>(null);

  const handleChatIdChange = useCallback(
    async (newChatId: string | null) => {
      setCurrentChatId(newChatId);
      try {
        if (options?.onChatIdPersist) {
          await options.onChatIdPersist(newChatId);
        }
      } catch (error) {
        // console.error("Failed to persist chatId change:", error);
      }
    },
    [options?.onChatIdPersist]
  );

  const fetchAvailableChats = useCallback(async () => {
    if (!workproductId) {
      setIsConversationLoading(false);
      return;
    }
    try {
      const response = await fetch(
        `${backendUrl}/api/agentchat/workproduct/chats`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ workproductId }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        const chats = (data.chats || []) as ChatInfo[];
        setAvailableChats(chats);
        // A workproduct with no chats is ready to show its welcome state.
        // When chats exist, keep the loader visible until the selected chat's
        // history request finishes.
        if (chats.length === 0) setIsConversationLoading(false);
      } else {
        setIsConversationLoading(false);
      }
    } catch (error) {
      // console.error("Error fetching available chats:", error);
      setIsConversationLoading(false);
    }
  }, [backendUrl, workproductId]);

  const hooks: ChatManagementHooks = useMemo(
    () => ({
      onChatIdChange: async (chatId: string | null) => {
        await handleChatIdChange(chatId);
      },
      onRefreshChats: async () => {
        await fetchAvailableChats();
      },
    }),
    [fetchAvailableChats, handleChatIdChange]
  );

  const handleNewMessage = useCallback((message: any) => {
    // Minimal, targeted logs
    if (process.env.NODE_ENV !== "production") {
      const isAssistantUpdate =
        message.role === "assistant" && message.id?.startsWith("update_");
      if (isAssistantUpdate) {
        // // console.log(`🧵 assistant chunk len=${(message.content || "").length}`);
      } else {
        // // console.log(
        // //   `💬 message role=${message.role} id=${message.id} len=${(message.content || "").length}`
        // // );
      }
    }
    const mergeAssistantUpdate = (prev: any[]) => {
      // Extract anchor id from update message id format: update_<anchorId>_<timestamp>
      const anchorMatch =
        typeof message.id === "string"
          ? message.id.match(/^update_(.+?)_\d+$/)
          : null;
      const anchorId = anchorMatch ? anchorMatch[1] : null;

      if (anchorId) {
        const index = prev.findIndex(
          m => m.role === "assistant" && m.id === anchorId
        );
        if (index >= 0) {
          const updated = [...prev];
          const target = prev[index];
          updated[index] = {
            ...target,
            content: (target.content || "") + (message.content || ""),
            timestamp: message.timestamp,
          };
          return updated;
        }
      }

      // Fallback: merge into the last assistant if anchor not found
      const last = prev[prev.length - 1];
      if (last && last.role === "assistant") {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...last,
          content: (last.content || "") + (message.content || ""),
          timestamp: message.timestamp,
        };
        return updated;
      }
      return [...prev, message];
    };

    // Handle metadata-only updates (e.g., attaching thinkingContent to an
    // existing message, or updating a tool_call loading message with its result).
    // Format: id = "meta_<targetId>", with extra fields to merge onto the target.
    //
    // If the target message doesn't exist (e.g., Gemini didn't emit a prior
    // toolCalled event so no loading message was created), fall back to creating
    // a new standalone message from the meta fields rather than silently dropping it.
    if (typeof message.id === "string" && message.id.startsWith("meta_")) {
      const targetId = message.id.replace(/^meta_/, "");
      setMessages(prev => {
        const index = prev.findIndex(m => m.id === targetId);
        if (index >= 0) {
          // Target exists — merge fields in-place
          const updated = [...prev];
          const { id: _id, ...fields } = message;
          updated[index] = { ...updated[index], ...fields };
          return updated;
        }
        // Target not found — create a new message from the meta fields.
        // This handles the case where toolCompleted arrives without a prior
        // toolCalled (e.g., Gemini, or when events arrive out of order).
        const { id: _id, ...fields } = message;
        const newMsg = {
          id: targetId,
          role: (fields as any).role || "tool",
          content: (fields as any).content || "",
          timestamp: new Date(),
          ...fields,
        };
        return [...prev, newMsg];
      });
      return;
    }

    if (
      message.role === "assistant" &&
      typeof message.id === "string" &&
      message.id.startsWith("update_")
    ) {
      // Ensure at most one synchronous flush per frame to reduce app-wide impact
      if (!didFlushThisFrameRef.current) {
        didFlushThisFrameRef.current = true;
        if (!resetFlushFlagScheduledRef.current) {
          resetFlushFlagScheduledRef.current = true;
          requestAnimationFrame(() => {
            didFlushThisFrameRef.current = false;
            resetFlushFlagScheduledRef.current = false;
          });
        }
        flushSync(() => {
          setMessages(prev => mergeAssistantUpdate(prev));
        });
        return;
      }
      // Already flushed this frame; allow batched update for the rest
      setMessages(prev => mergeAssistantUpdate(prev));
      return;
    }

    // Non-streaming messages
    setMessages(prev => {
      if (process.env.NODE_ENV !== "production") {
      }
      return [...prev, message];
    });
  }, []);

  const loadChatHistory = useCallback(
    async (chatId: string) => {
      if (!chatId) {
        setMessages([]);
        setIsConversationLoading(false);
        return;
      }
      setIsConversationLoading(true);
      try {
        const historyEndpoint = `${backendUrl}/api/document-agent/history`;

        const response = await fetch(historyEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ chatId, workproductId }),
        });

        if (response.ok) {
          const data = await response.json();
          // LangChain endpoint returns { messages: [...] }, traditional returns { history: [...] }
          const rawHistory = data.messages || data.history || [];
          // Convert relative proxy paths in imageUrls to absolute URLs
          const history = rawHistory.map((msg: any) => {
            if (Array.isArray(msg.imageUrls) && msg.imageUrls.length > 0) {
              return {
                ...msg,
                imageUrls: msg.imageUrls.map((url: string) =>
                  typeof url === "string" && url.startsWith("/api/image-proxy/")
                    ? `${backendUrl}${url}`
                    : url
                ),
              };
            }
            return msg;
          });
          setMessages(prev => {
            // If there are pending messages (e.g., user just sent one) that aren't in history yet,
            // preserve the current state to avoid losing unsent messages during race conditions.
            // This only applies within the same chat - switching chats clears messages first.
            if (prev.length > 0 && history.length < prev.length) {
              const lastPrev = prev[prev.length - 1];
              const lastPrevIsUser =
                lastPrev?.role === "user" &&
                typeof lastPrev?.content === "string";
              if (lastPrevIsUser) {
                const existsInHistory = history.some(
                  (h: any) =>
                    h.role === "user" && h.content === lastPrev.content
                );
                if (!existsInHistory) {
                  return prev;
                }
              }
            }
            return history;
          });
        } else {
          // console.error("Failed to load chat history");
          setMessages([]);
        }
      } catch (error) {
        // console.error("Error loading chat history:", error);
        setMessages([]);
      } finally {
        setIsConversationLoading(false);
      }
    },
    [backendUrl, workproductId, workproductType]
  );

  useEffect(() => {
    setIsConversationLoading(Boolean(workproductId));
  }, [workproductId]);

  const createChat = useCallback(async (): Promise<string | null> => {
    if (!workproductId) return null;
    const newId = await createNewChat(
      workproductId,
      workproductType,
      backendUrl,
      hooks
    );
    if (newId && messages.length > 0) {
      suppressNextHistoryLoadRef.current = true;
    }
    return newId;
  }, [backendUrl, hooks, messages.length, workproductId, workproductType]);

  const switchChat = useCallback(
    (newChatId: string) => {
      switchToChatApi(newChatId, hooks);
    },
    [hooks]
  );

  const deleteChat = useCallback(
    async (chatId: string): Promise<boolean> => {
      const success = await deleteChatApi(
        chatId,
        workproductId,
        backendUrl,
        hooks
      );
      if (success && currentChatId === chatId) {
        await handleChatIdChange(null);
      }
      return success;
    },
    [backendUrl, currentChatId, hooks, workproductId, handleChatIdChange]
  );

  const ensureChatExists = useCallback(
    async (chatId?: string): Promise<string> => {
      const id = await ensureChatExistsApi(
        chatId,
        workproductId,
        workproductType,
        backendUrl,
        hooks
      );
      if (!chatId && messages.length > 0) {
        suppressNextHistoryLoadRef.current = true;
      }
      return id;
    },
    [backendUrl, hooks, messages.length, workproductId, workproductType]
  );

  useEffect(() => {
    if (!workproductId) return;
    if (currentChatId) return;
    if (availableChats.length === 0) return;
    if (hasAutoSelectedRef.current) return;
    if (options?.autoSelectLatest === false) return;

    const latest = availableChats.reduce((acc, c) =>
      new Date(c.createdAt) > new Date(acc.createdAt) ? c : acc
    );
    hasAutoSelectedRef.current = true;
    switchChat(latest.chatId);
  }, [
    availableChats,
    currentChatId,
    options?.autoSelectLatest,
    switchChat,
    workproductId,
  ]);

  useEffect(() => {
    if (!workproductId) return;
    if (availableChats.length > 0) return;
    if (didRetryChatsRef.current) return;
    didRetryChatsRef.current = true;
    const t = setTimeout(() => {
      fetchAvailableChats();
    }, 1000);
    return () => clearTimeout(t);
  }, [availableChats.length, fetchAvailableChats, workproductId]);

  useEffect(() => {
    const previousChatId = previousChatIdRef.current;
    const isSwitchingChat =
      previousChatId !== null && previousChatId !== currentChatId;

    // Update ref for next comparison
    previousChatIdRef.current = currentChatId;

    if (!currentChatId) {
      setMessages([]);
      return;
    }

    // When switching to a different chat, clear messages first
    if (isSwitchingChat) {
      setMessages([]);
    }

    if (suppressNextHistoryLoadRef.current) {
      suppressNextHistoryLoadRef.current = false;
      return;
    }
    loadChatHistory(currentChatId);
  }, [currentChatId, loadChatHistory]);

  useEffect(() => {
    if (workproductId) {
      fetchAvailableChats();
    }
  }, [fetchAvailableChats, workproductId]);

  return {
    currentChatId,
    messages,
    availableChats,
    isConversationLoading,
    handleChatIdChange,
    handleNewMessage,
    fetchAvailableChats,
    loadChatHistory,
    createChat,
    switchChat,
    deleteChat,
    ensureChatExists,
  };
}
